/**
 * rolloutMode -- which shape the "Deploy plan" modal takes for a site's VLAN rollout.
 *
 * Owner directive (2026-09-14): "we should not have this option cause vlan should always sync to
 * devices". Auto-deploy is on by default and keeps every gateway and switch in sync on its own, so
 * the modal must NOT present a manual confirm gate in that state -- it would contradict the
 * automatic behaviour. This pure helper decides the mode from the two facts the panel already has
 * (is auto-deploy enabled for the site, and how the last run ended), so the decision is unit
 * tested here instead of tangled into the component:
 *
 *   "preview"  Auto-deploy ON and not sitting on a failed run: a READ-ONLY preview of what
 *              auto-deploy applies. No "I reviewed..." checkbox, no "Apply to N devices" button.
 *   "manual"   Auto-deploy OFF: the operator opted into applying by hand, so the review checkbox
 *              and Apply button stay exactly as before.
 *   "retry"    Auto-deploy ON but the last automatic run FAILED / was REFUSED / was interrupted:
 *              a plain "Retry now" (no review checkbox -- the operator is re-running the same
 *              automatic plan, not hand-approving a new one).
 *
 * A run still in progress is "preview": the buttons are irrelevant while it runs and the live
 * progress is what matters.
 */

/** The rollout status the auto-deploy view reports for the last run (VlanRollout.status). */
export type LastRunStatus =
  | "running"
  | "succeeded"
  | "failed"
  | "interrupted"
  | "refused"
  | null
  | undefined;

export type RolloutMode = "preview" | "manual" | "retry";

/** Terminal states of an automatic run that leave devices possibly out of sync -> offer a retry. */
const FAILED_STATES: ReadonlySet<string> = new Set(["failed", "interrupted", "refused"]);

/**
 * The modal mode. `autoEnabled` is the site's auto-deploy on/off flag; `lastRunStatus` is the
 * newest run's status (any trigger), or null when the site has never run one.
 */
export function rolloutMode(autoEnabled: boolean, lastRunStatus: LastRunStatus): RolloutMode {
  // Manual is the operator's explicit opt-out of automatic sync: honour it above everything.
  if (!autoEnabled) return "manual";
  // Auto-deploy left the devices on a failed/refused run: a bare retry, no review gate.
  if (lastRunStatus != null && FAILED_STATES.has(lastRunStatus)) return "retry";
  // The normal case: auto-deploy is keeping devices in sync; the modal only previews.
  return "preview";
}

export interface RolloutCopy {
  /** Modal / card title. */
  title: string;
  /** One sentence under it, honest about whether anything here is sent by hand. */
  subtitle: string;
}

/** Header copy for each mode. Replaces the old "nothing here is sent unless you confirm" line. */
export function rolloutCopy(mode: RolloutMode): RolloutCopy {
  switch (mode) {
    case "manual":
      return {
        title: "Deploy plan",
        subtitle:
          "Auto-deploy is off for this site, so nothing is sent until you review the plan and " +
          "apply it. Turn auto-deploy on to keep devices in sync automatically.",
      };
    case "retry":
      return {
        title: "Deploy plan",
        subtitle:
          "The last automatic deploy did not finish. Review the reason below and retry; " +
          "auto-deploy keeps the devices in sync once it succeeds.",
      };
    case "preview":
    default:
      return {
        title: "Deploy plan",
        subtitle:
          "Auto-deploy keeps every gateway and switch in sync and applies VLAN changes for you. " +
          "This is a read-only preview of what it deploys -- nothing here needs your approval.",
      };
  }
}

/** Whether the manual review checkbox + "Apply to N devices" button show (manual mode only). */
export function showsManualApply(mode: RolloutMode): boolean {
  return mode === "manual";
}

/** Whether the plain "Retry now" button shows (a failed/refused automatic run). */
export function showsRetry(mode: RolloutMode): boolean {
  return mode === "retry";
}
